import { Modal, Table, Button, Spinner, Alert, Badge } from "react-bootstrap";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaLock,
  FaUnlock,
} from "react-icons/fa";
import {
  getImeiLock,
  imeiLock as imeiLockThunk,
} from "../../../../redux/slices/sims";
import NotyfContext from "../../../../contexts/NotyfContext";

const ImeiLockModal = ({ showImeiLockModal, setShowImeiLockModal, sim }) => {
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);

  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const imeiLockState = useSelector((state) => state.sims.imeiLock);

  const [activeUser, setActiveUser] = useState({});
  const [initialLoading, setInitialLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setActiveUser(impersonationUser?.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    let mounted = true;
    const fetchImeiLock = async () => {
      if (!showImeiLockModal || !sim?.imsi) return;
      setErrorMsg("");
      setInitialLoading(true);
      try {
        await dispatch(getImeiLock(sim.imsi)).unwrap?.();
      } catch (err) {
        if (!mounted) return;
        setErrorMsg("Unable to fetch IMEI Lock status. Please try again.");
      } finally {
        if (mounted) setInitialLoading(false);
      }
    };
    fetchImeiLock();
    return () => {
      mounted = false;
    };
  }, [dispatch, showImeiLockModal, sim?.imsi]);

  const isLocked = useMemo(() => {
    if (!imeiLockState) return false;
    else return true;
  }, [imeiLockState]);

  const currentImei =
    imeiLockState?.imei ?? imeiLockState?.currentImei ?? undefined;

  const dynamicStyle = {
    "--dynamic-bg-color": whitelabel?.backgroundColor ?? "#0E1A2B",
    "--dynamic-font-color": whitelabel?.fontColor ?? "#ffffff",
    border: "none",
  };

  const handleClose = () => {
    setShowImeiLockModal(false);
    setActionLoading(false);
    setErrorMsg("");
  };

  const handlePrimaryAction = async () => {
    if (!sim?.imsi) return;
    setActionLoading(true);
    setErrorMsg("");
    try {
      await imeiLockThunk(sim.imsi)
        .then(() => {
          notyf.open({
            type: "success",
            message: isLocked
              ? "IMEI Lock removed successfully"
              : "IMEI Lock added successfully",
            ripple: true,
            dismissible: false,
            duration: 4500,
            position: { x: "center", y: "top" },
          });
        })
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: "Something went wrong",
            duration: 4500,
            position: {
              x: "center",
              y: "top",
            },
            ripple: true,
            dismissible: false,
          });
        })
        .finally(async () => {
          await dispatch(getImeiLock(sim.imsi)).unwrap?.();
          setShowImeiLockModal(false);
        });
    } catch (err) {
      console.log(err);
      setErrorMsg(
        "Something went wrong. Please try again or contact the administrator."
      );
      notyf.open({
        type: "danger",
        message: errorMsg,
        ripple: true,
        dismissible: false,
        duration: 5000,
        position: { x: "center", y: "top" },
      });
    } finally {
      setActionLoading(false);
      setShowImeiLockModal(false);
    }
  };

  const headerBadge = (
    <Badge bg={isLocked ? "success" : "secondary"} className="ms-2">
      {isLocked ? "Active" : "Not active"}
    </Badge>
  );

  const StatusCallout = () => {
    if (initialLoading) {
      return (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Loading current status…</span>
        </div>
      );
    }

    if (isLocked) {
      return (
        <Alert
          variant="success"
          className="d-flex align-items-center gap-2 mb-3"
        >
          <FaLock />
          <div>
            <strong>An active IMEI Lock is present</strong>
            {currentImei ? (
              <>
                <br />
                Locked IMEI: <code>{currentImei}</code>
              </>
            ) : null}
          </div>
        </Alert>
      );
    }

    return (
      <Alert variant="info" className="d-flex align-items-center gap-2 mb-3">
        <FaInfoCircle />
        <div>
          <strong>No active IMEI Lock</strong>
          <br />
          You can add an IMEI Lock for this SIM.
        </div>
      </Alert>
    );
  };

  return (
    <Modal
      show={showImeiLockModal}
      onHide={handleClose}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <div className="d-flex align-items-center">
          <span>IMEI Lock</span>
          {headerBadge}
        </div>
      </Modal.Header>
      <Modal.Body>
        <StatusCallout />

        {errorMsg ? (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <FaExclamationCircle />
            <div>{errorMsg}</div>
          </Alert>
        ) : null}

        <Table responsive striped size="sm" className="mb-0">
          <thead>
            <tr>
              <th>IMSI</th>
              <th>ICCID</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{sim?.imsi ?? "—"}</td>
              <td>{sim?.iccid ?? "—"}</td>
            </tr>
          </tbody>
        </Table>

        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          <FaInfoCircle className="me-1" />
          The action will apply to this subscriber. Please make sure the details
          are correct.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          style={dynamicStyle}
          disabled={actionLoading || initialLoading}
        >
          Close
        </Button>
        <Button
          onClick={handlePrimaryAction}
          style={dynamicStyle}
          disabled={actionLoading || initialLoading || !sim?.imsi}
        >
          {actionLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Please wait…
            </>
          ) : (
            <>
              {isLocked ? (
                <FaUnlock className="me-2" />
              ) : (
                <FaLock className="me-2" />
              )}
              {isLocked ? "Remove IMEI Lock" : "Add IMEI Lock"}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImeiLockModal;
